import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Search, Download, Calendar, MapPin, Clock, Plus, ChevronDown } from 'lucide-react';
import Navigation from '../components/Navigation';
import { mockData } from '../mock';

const SchedulePage = () => {
  const [selectedDay, setSelectedDay] = useState('day1');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedSession, setExpandedSession] = useState(null);

  const categories = ['all', 'Keynote', 'Workshop', 'Worship', 'Panel', 'Networking', 'Interactive', 'Special'];
  
  const categoryColors = {
    'Keynote': 'bg-[#CFA64B] text-[#0B1B3B]',
    'Workshop': 'bg-blue-500 text-white',
    'Worship': 'bg-purple-500 text-white',
    'Panel': 'bg-green-500 text-white',
    'Networking': 'bg-orange-500 text-white',
    'Interactive': 'bg-pink-500 text-white',
    'Special': 'bg-red-500 text-white'
  };

  const currentDayData = mockData.scheduleData[selectedDay];
  
  const filteredSessions = currentDayData.sessions.filter(session => {
    const matchesSearch = searchQuery === '' || 
      session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.speaker.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || session.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const featuredSessions = Object.values(mockData.scheduleData)
    .flatMap(day => day.sessions)
    .filter(session => session.featured);

  const getTimeOfDay = (time) => {
    const hour = parseInt(time.split(':')[0]);
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    return 'Evening';
  };

  const groupedSessions = filteredSessions.reduce((acc, session) => {
    const timeOfDay = getTimeOfDay(session.time);
    if (!acc[timeOfDay]) acc[timeOfDay] = [];
    acc[timeOfDay].push(session);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Header */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <div className="flex items-center justify-center gap-2 text-sm mb-6 text-gray-300">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <a href="/experience" className="hover:text-[#CFA64B] transition-colors">Experience</a>
            <span>/</span>
            <span className="text-white font-medium">Event Schedule</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Event <span className="text-[#CFA64B]">Schedule</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Three days of inspiration, connection, and transformation.
          </p>
          
          <div className="flex flex-wrap gap-3 justify-center mb-6">
            {Object.keys(mockData.scheduleData).map((day) => (
              <Button
                key={day}
                onClick={() => setSelectedDay(day)}
                className={`${
                  selectedDay === day
                    ? 'bg-[#CFA64B] text-[#0B1B3B]'
                    : 'bg-white/10 hover:bg-white/20 text-white border-white/30'
                } font-semibold px-6 py-6 text-lg`}
              >
                {mockData.scheduleData[day].name}
              </Button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/40 backdrop-blur-sm">
              <Download className="w-4 h-4 mr-2" />
              Download Schedule
            </Button>
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-semibold">
              Buy Tickets
            </Button>
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="sticky top-[80px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1440px] mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 w-full md:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search sessions or speakers"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 pr-4 py-2.5 rounded-full border-gray-300 focus:border-[#CFA64B] focus:ring-[#CFA64B]"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide w-full md:w-auto">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                    selectedCategory === cat
                      ? 'bg-[#CFA64B] text-[#0B1B3B]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'All Sessions' : cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Featured Sessions Spotlight */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Don't Miss These Moments</h2>
            <p className="text-xl text-gray-600">Highlighted sessions you can't afford to miss</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredSessions.map((session) => (
              <Card
                key={session.id}
                className="overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-500 border-2 border-[#CFA64B]/20 hover:border-[#CFA64B]"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img
                    src={session.image}
                    alt={session.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${categoryColors[session.category]}`}>
                      {session.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <Clock className="w-4 h-4" />
                    <span>{session.time}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0B1B3B] mb-2 group-hover:text-[#CFA64B] transition-colors">
                    {session.title}
                  </h3>
                  <p className="text-gray-600 mb-3">{session.speaker}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MapPin className="w-4 h-4" />
                    <span>{session.venue}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Schedule Timeline */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-[#0B1B3B] mb-2">
              {currentDayData.name} Schedule
            </h2>
            <p className="text-xl text-gray-600">{currentDayData.date}</p>
          </div>

          {Object.keys(groupedSessions).length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2">No sessions found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
              <Button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="space-y-16">
              {['Morning', 'Afternoon', 'Evening'].map((timeOfDay) => (
                groupedSessions[timeOfDay] && (
                  <div key={timeOfDay} className="relative">
                    {/* Time of Day Header */}
                    <div className="flex items-center gap-4 mb-8">
                      <div className="relative">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0B1B3B] to-[#1e3a8a] flex items-center justify-center">
                          <span className="text-white font-bold text-sm">{timeOfDay}</span>
                        </div>
                      </div>
                      <div className="flex-1 h-1 bg-gradient-to-r from-[#CFA64B] via-gray-300 to-gray-200 rounded-full"></div>
                    </div>

                    {/* Sessions */}
                    <div className="space-y-6 ml-20">
                      {groupedSessions[timeOfDay].map((session, idx) => (
                        <Card
                          key={session.id}
                          className={`border-l-4 hover:shadow-xl transition-all duration-300 cursor-pointer ${
                            expandedSession === session.id ? 'shadow-xl' : ''
                          }`}
                          style={{ borderLeftColor: session.category === 'Keynote' ? '#CFA64B' : '#0B1B3B' }}
                          onClick={() => setExpandedSession(expandedSession === session.id ? null : session.id)}
                        >
                          <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-3">
                                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${categoryColors[session.category]}`}>
                                    {session.category}
                                  </span>
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Clock className="w-4 h-4" />
                                    <span className="font-semibold">{session.time}</span>
                                  </div>
                                </div>

                                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2 hover:text-[#CFA64B] transition-colors">
                                  {session.title}
                                </h3>
                                
                                <div className="flex flex-wrap items-center gap-4 text-gray-600">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold">Speaker:</span>
                                    <span>{session.speaker}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />
                                    <span>{session.venue}</span>
                                  </div>
                                </div>

                                {/* Expanded Description */}
                                {expandedSession === session.id && session.description && (
                                  <div className="mt-4 pt-4 border-t border-gray-200">
                                    <p className="text-gray-700 leading-relaxed">{session.description}</p>
                                  </div>
                                )}
                              </div>

                              <div className="flex md:flex-col gap-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-[#0B1B3B]"
                                >
                                  <Plus className="w-4 h-4 mr-1" />
                                  Add
                                </Button>
                                {session.description && (
                                  <button className="text-sm text-gray-500 hover:text-[#CFA64B] flex items-center gap-1">
                                    <ChevronDown className={`w-4 h-4 transition-transform ${
                                      expandedSession === session.id ? 'rotate-180' : ''
                                    }`} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Experience the Energy */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Experience the Energy</h2>
            <p className="text-xl text-gray-600">Inspiration on Every Stage</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&h=400&fit=crop',
              'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=600&h=400&fit=crop',
              'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=600&h=400&fit=crop',
              'https://images.unsplash.com/photo-1470229538611-16ba8c7ffbd7?w=600&h=400&fit=crop'
            ].map((img, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-2xl aspect-[4/3] group cursor-pointer shadow-lg hover:shadow-2xl transition-all">
                <img
                  src={img}
                  alt={`Experience ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Plan Your Experience CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Your journey starts here
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Plan your schedule, meet the speakers, and be part of the movement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-semibold px-8 py-6 text-lg">
              View Speakers
            </Button>
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-semibold px-8 py-6 text-lg">
              Get Tickets
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
              Conference Info
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default SchedulePage;
